<template>
  <Loader :show="pageLoading" v-if="pageLoading" />
  <MDBCard class="border-0" v-else>
    <CategoryTop small :title="$t('account.profile.title')" :breadcrumbs-parent-routes="['account']"
      :description="$t('account.profile.description')" content-class="px-4 mt-4" left-col="6" right-col="6">
      <template #right>
        <MDBRow>
          <MDBCol col="12" class="mb-5">
            <div class="progress">
              <div class="progress-bar bg-danger progress-bar-striped progress-bar-animated" role="progressbar"
                :style="'width:' + fillPercent + '%'" :aria-valuenow="fillPercent" aria-valuemin="0"
                aria-valuemax="100">
                {{ fillPercent }}%
              </div>
            </div>
          </MDBCol>
        </MDBRow>
      </template>
    </CategoryTop>
    <MDBTabs v-model="activeTabId">
      <MDBRow>
        <MDBCol md="12" class="px-5 pb-4">
          <MDBTabNav tabsClasses="profile-tabs" pills>
            <MDBTabItem tabId="general" href="general" class="text-center py-1" :class="`${activeTabId === 'general'
              ? 'bg-dark text-white'
              : 'bg-secondary-gray text-dark'
              }`">
              <div class="item--height d-flex justify-content-center align-items-center"
                v-html="$t('account.profile.general')"></div>
            </MDBTabItem>
            <MDBTabItem tabId="preference" href="preference" class="text-center py-1" :class="`${activeTabId === 'preference'
              ? 'bg-dark text-white'
              : 'bg-secondary-gray text-dark'
              }`">
              <div class="item--height d-flex justify-content-center align-items-center">
                {{ $t("account.profile.activityAndProfile") }}
              </div>
            </MDBTabItem>
          </MDBTabNav>
        </MDBCol>
      </MDBRow>

      <MDBTabContent>
        <MDBTabPane tabId="general">
          <VeeForm class="form user-form" @submit="onSubmit">
            <MDBRow>
              <MDBCol xl="4" md="6" class="mb-4">
                <PersonalData ref="personalData"></PersonalData>
              </MDBCol>

              <MDBCol xl="4" md="6" class="mb-4">
                <OperatingData ref="operatingData" :requiredSection="true"></OperatingData>
              </MDBCol>

              <MDBCol xl="3" md="6" class="mb-4">
                <div class="switch-wrapper d-flex flex-column justify-content-center pt-4">
                  <div class="form-check form-switch">
                    <input id="switch_addr" v-model="diffDeliveryAddress" class="form-check-input" type="checkbox"
                      role="switch" name="switch_addr" />
                    <label class="form-check-label font-12" for="switch_addr">{{
                      $t("account.profile.diffDeliveryAddres")
                    }}</label>
                  </div>
                </div>
                <DeliveryData v-show="diffDeliveryAddress" ref="deliveryData"></DeliveryData>
              </MDBCol>

              <MDBCol xl="12" md="6" class="mb-4" v-if="showTenantEmails()">
                <TenantEmail ref="tenantEmails" />
              </MDBCol>
            </MDBRow>

            <MDBRow>
              <MDBCol col="12" class="mb-4">
                <IndicatorsData ref="indicatorsData"></IndicatorsData>
              </MDBCol>
            </MDBRow>
            <MDBRow>
              <MDBCol col="12" class="text-start">
                <div v-if="me.salespersons_dealers && me.salespersons_dealers.length > 0" class="pb-4">
                  <div v-for="(seller, index) in me.salespersons_dealers" :key="index">
                    <div v-if="!seller.is_internal">
                      <span class="fw-bold">{{ $t("contact.form.contactPerson") }}:</span>
                      {{ seller.name }}
                      <a :href="'mailto:' + seller.email">{{ seller.email }}</a>
                    </div>
                  </div>
                </div>
                <MDBBtn type="submit" color="danger" :disabled="loading"
                  class="d-flex justify-content-center align-items-center">
                  <span>{{ $t("global.buttons.send") }}</span>
                  <Loader :show="loading" :small="true" spinner="spinner.svg" :tiny="true" />
                </MDBBtn>
                <p v-if="apiError" class="text-danger text-small text-end m-0 pt-2 text-small">
                  {{ apiError }}
                </p>
              </MDBCol>
            </MDBRow>
          </VeeForm>
        </MDBTabPane>
        <MDBTabPane tabId="preference">
          <LiteVersionPreferences ref="preferencesRef" @requestSave="onSubmit"
            @customerNumberExists="activeTabId = 'preference'" />
        </MDBTabPane>
      </MDBTabContent>
    </MDBTabs>
  </MDBCard>
</template>

<script setup>
import { Form as VeeForm } from "vee-validate";
import CategoryTop from "@/components/General/CategoryTop/index.vue";
import {
  MDBRow,
  MDBCol,
  MDBCard,
  MDBBtn,
  MDBTabs,
  MDBTabNav,
  MDBTabItem,
  MDBTabContent,
  MDBTabPane,
} from "mdb-vue-ui-kit";
import { useStore } from "vuex";
import { ref, onMounted, computed, watch, toRaw } from "vue";
import PersonalData from "@/components/User/ProfileForms/PersonalData.vue";
import OperatingData from "@/components/User/ProfileForms/OperatingData.vue";
import IndicatorsData from "@/components/User/ProfileForms/IndicatorsData.vue";
import TenantEmail from "@/components/User/ProfileForms/TenantEmail.vue";
import DeliveryData from "@/components/User/ProfileForms/DeliveryData.vue";
import LiteVersionPreferences from "@/components/User/ProfileForms/LiteVersionPreferences.vue";
import Loader from "@/components/General/Loader/index.vue";

import "vue3-toastify/dist/index.css";

import { ProfileService } from "@/services/profile.service";
import { UserService } from "@/services/user.service";
import { useTenantConfig } from "@/composables/useTenantConfig";
import { userCanAddTenantEmails } from "@/composables/useProfileFieldCheck";

import { afterSuccess } from "@/composables/profile.js";
import { useProfileFormData } from "@/composables/useProfileFormData";
import { useProfileFillPercent } from "@/composables/useProfileFillPercent";

const store = useStore();
const { me, userHashId, profileHashId, userPlainId, loadProfileForm } =
  useProfileFormData(store);
const { tenantConfig } = useTenantConfig();
const { showTenantEmails } = userCanAddTenantEmails(tenantConfig);

const personalData = ref(null);
const operatingData = ref(null);
const indicatorsData = ref(null);
const deliveryData = ref(null);
const diffDeliveryAddress = ref(false);
const tenantEmails = ref([]);
const apiError = ref(null);
const conditions = ref(false);
const isWholesaler = ref(false);
const isWholesalerStaff = ref(false);
const isStaff = ref(false);
const loading = computed(() => store.getters.getPending);
const activeTabId = ref("general");
const pageLoading = ref(true);
const preferencesRef = ref(null);


const onSubmit = async () => {
  const { email, id, ...personal } = personalData.value.models;
  const { ...operating } = operatingData.value.models;
  const { lang, ...mainAddress } = operating;

  let args = {
    id,
    user: {
      email,
      country: operating.country,
      lang,
      vat: operating.vat,
      is_active: true,
    },
    profile: {
      user_id: id,
      ...personal,
      ...mainAddress,
      ...indicatorsData.value.models,
      conditions: conditions.value
    },
    addresses: [mainAddress],
  };

  if (diffDeliveryAddress.value) {
    args.addresses.push(toRaw(deliveryData.value.models));
  }

  if (!diffDeliveryAddress.value && deliveryData.value?.models?.id) {
    deliveryData.value.models.to_remove = true;
    args.addresses.push(toRaw(deliveryData.value.models));
  }


  if (args.profile.mobile_phone !== null && args.profile.mobile_phone !== undefined) {
    args.profile.mobile_phone = String(args.profile.mobile_phone);
  }

  const response = await store.dispatch("updateUser", args);

  await ProfileService.storeStaffToDealer({
    dealer_id: me.value.plainId,
    postal_code: operating.postal_code,
  });

  if (response.status === 200) {
    if (showTenantEmails()) {
      const data = {
        user_id: me.value.plainId,
        tenant_emails: tenantEmails.value.emails,
      };
      await UserService.tenantEmail(data);
    }

    let preferencesOk = true;
    if (preferencesRef.value?.persistPreferences) {
      preferencesOk = await preferencesRef.value.persistPreferences({
        refresh: false,
        showToast: false,
      }) !== false;
    }

    if (preferencesOk) {
      await loadProfileForm();
      apiError.value = null;
    }

    await afterSuccess(
      userHashId.value,
      profileHashId.value,
      fillPercent.value,
      userPlainId.value,
      store
    );
  } else {
    apiError.value = response.data.message;
  }
};

const { fillPercent } = useProfileFillPercent(() => [
  personalData.value?.percentage,
  operatingData.value?.percentage,
  !diffDeliveryAddress.value ? 100 : deliveryData.value?.percentage,
  indicatorsData.value?.percentage,
]);

watch(me, async (newVal) => {
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isWholesaler.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesalerStaff")) {
    isWholesalerStaff.value = true;
  }
  if (newVal && newVal.roles.find((item) => item.name == "wholesaler")) {
    isStaff.value = true;
  }
});


onMounted(async () => {
  try {
    await loadProfileForm();
  } finally {
    pageLoading.value = false;
  }
});
</script>

<style scoped lang="scss">
@import "@/styles/_variables.scss";
@import "@/styles/profile.scss";

.item--height {
  min-height: 40px !important;
}
</style>
